import { createFileRoute, Link } from '@tanstack/react-router'
import { DocPage } from '../components/docs/DocPage'
import { Section, P, DefList, C, Pre, Automate, H3 } from '../components/docs/prose'

export const Route = createFileRoute('/docs/bills')({ component: Bills })

const SECTIONS = [
  { id: 'payee-vs-bill', title: 'Payee and bill' },
  { id: 'four-amounts', title: 'The four amounts' },
  { id: 'coverage', title: 'Coverage' },
  { id: 'revolving', title: 'Revolving balance' },
  { id: 'status', title: 'Status' },
]

function Bills() {
  return (
    <DocPage
      path="/docs/bills"
      title="Bills"
      lede={
        <>
          A bill has four different amounts attached to it, and collapsing them
          into one is how a tracker ends up telling you a credit card is paid
          when it is quietly accruing interest.
        </>
      }
      sections={SECTIONS}
    >
      <Section id="payee-vs-bill" title="Payee and bill">
        <P>
          The model has two levels. A <strong>payee</strong> is the recurring
          relationship — Hydro One, the Amex, the mortgage. A{' '}
          <strong>bill</strong> is one month of that relationship.
        </P>
        <DefList
          items={[
            [
              'Payee holds the stable facts',
              <>
                Category (<C>CreditCard</C>, <C>Housing</C>, <C>Utility</C>,{' '}
                <C>Telecom</C>, <C>Tax</C>, <C>Insurance</C>, <C>Vehicle</C>,{' '}
                <C>Other</C>), which account funds it, which liability it
                settles, the payment method, the typical day of the month, and
                who in the household it belongs to.
              </>,
            ],
            [
              'Bill holds the per-month facts',
              <>
                The period (always the first of the month), the due date, the
                amounts, when it was paid, from which account, and the link to
                the transaction that settled it.
              </>,
            ],
          ]}
        />
        <P>
          The split is what makes both of the questions you actually ask cheap:
          &ldquo;what is outstanding right now&rdquo; reads the current
          period&rsquo;s bills; &ldquo;what did insurance cost me this
          year&rdquo; groups bills by their payee&rsquo;s category. One flat
          table would make one of those a scan.
        </P>
        <P>
          A payee is unique by name within a household, and a bill is unique by
          payee and period. You cannot accidentally create two August Hydro
          bills.
        </P>
      </Section>

      <Section id="four-amounts" title="The four amounts">
        <P>
          These are kept as four separate columns because they disagree, and
          the disagreements are the interesting part.
        </P>
        <DefList
          items={[
            [<C>statement_balance</C>, 'What the biller says is owed for this period. On a card, this is the number that starts accruing interest if you do not clear it.'],
            [<C>minimum_due</C>, 'The smallest payment that avoids a late fee. Paying it is not the same as paying the bill, and it is the case most trackers cannot express.'],
            [<C>amount_paid</C>, 'What you say you paid. An assertion, not evidence.'],
            [<C>posted_amount</C>, 'What the bank actually took, with posted_on for when. Evidence.'],
          ]}
        />
        <P>
          A payment can post short, post twice, or post four days late.
          Treating <C>amount_paid</C> and <C>posted_amount</C> as one field
          means none of those are visible.{' '}
          <Link to="/docs/reconciliation" style={{ color: 'var(--accent-text)' }}>
            Reconciliation
          </Link>{' '}
          is the page about what happens when they disagree.
        </P>
      </Section>

      <Section id="coverage" title="Coverage">
        <P>
          Coverage answers &ldquo;how completely was the statement paid&rdquo;.
          It is computed from the amounts every time it is read, so it cannot
          go stale.
        </P>
        <DefList
          items={[
            [<C>unpaid</C>, 'Nothing paid.'],
            [<C>minimum</C>, 'At or near the minimum only. The rest revolves and starts costing you interest.'],
            [<C>partial</C>, 'More than the minimum, less than the statement balance.'],
            [<C>full</C>, 'Statement cleared. Also the answer when there is no statement balance to clear, such as a utility bill.'],
            [<C>over</C>, 'Paid more than owed — a prepayment, or a credit on the account.'],
          ]}
        />
        <P>
          There is a one-cent tolerance on <C>full</C> in both directions.
          Rounding on a transfer should not make a cleared card read as
          &ldquo;did not pay in full&rdquo; forever.
        </P>
        <H3>Worked example</H3>
        <Pre>{`statement_balance   2,418.55
minimum_due            50.00
amount_paid            50.00

coverage            minimum
revolving           2,368.55`}</Pre>
        <P>
          A tracker with one amount field would show this as paid.
        </P>
      </Section>

      <Section id="revolving" title="Revolving balance">
        <P>
          Revolving is simply <C>statement_balance − amount_paid</C>, floored
          at zero. It is the amount that carried over, which on a card is the
          amount interest is charged on.
        </P>
        <P>
          Whether the question even applies is a property of the payee, not the
          bill: <C>carries_balance</C> is true for credit cards and false for a
          hydro bill, because a utility does not revolve — it just goes unpaid.
          Bills on revolving payees are indexed separately so &ldquo;what is
          costing me interest this month&rdquo; is a cheap query rather than a
          scan of every bill you have ever had.
        </P>
      </Section>

      <Section id="status" title="Status">
        <P>
          Status is the calendar view of a bill, separate from coverage and
          from reconciliation. Stored values are <C>Paid</C>,{' '}
          <C>NoBalance</C> and <C>Skipped</C>. Anything else is resolved
          against today&rsquo;s date when you read it: <C>Overdue</C> if the
          due date has passed, <C>Due</C> if it is today, <C>Upcoming</C> if it
          is ahead.
        </P>
        <P>
          Resolving on read rather than storing it is what stops the list view
          and the{' '}
          <Link to="/docs/payment-grid" style={{ color: 'var(--accent-text)' }}>
            payment grid
          </Link>{' '}
          disagreeing about whether something is overdue — there is no
          background job whose last run defines the answer.
        </P>
      </Section>

      <Automate>
        You should not be creating payees by hand. FolioWorth reads twelve
        months of your outflows and proposes the ones that recur — three
        distinct months minimum, and an amount stable to within 25% — with a
        guessed category and typical due day. See{' '}
        <Link to="/docs/automating" style={{ color: 'var(--accent-text)' }}>
          detected bills
        </Link>.
      </Automate>
    </DocPage>
  )
}
